import { useMemo } from "react";
import Badge from "react-bootstrap/Badge";
import { resources } from "@constants/hardcoded-strings";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedAxnAdminUser, FormattedAxnAdminUserDetails } from "@idw-types/data/axn-admin-user";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { IDWSession } from "@idw-types/data/idw-session";
import { extractRelyingPartiesForPropertyName, isAxnAdminUser } from "@utils/permissions-util";
import { sortStrings } from "@utils/string-util";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import styles from "./organization-users-table-container.less";
import { OrganizationUsersTableCell } from "@containers/organization/organization-users/organization-users-table-cell/organization-users-table-cell";
import { AccessorKeyColumnDef, createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { formatDateToString, sortDates } from "@utils/date-util";
import { DISPLAY_DATE } from "@constants/date-formats";
import { OrganizationUsersNameEmailLabel } from "@containers/organization/organization-users/organization-users-table-container/organization-users-name-email-label/organization-users-name-email-label";

type OrganizationUsersTableContainerProps = {
    session: IDWSession;
    axnAdminUsers: Array<FormattedAxnAdminUser>;
    axnRelyingParties: Array<AxnRelyingParty>;
    tableState: TableState;
    isPageHeight: boolean;
};

type OrganizationUserTableDataRow = FormattedAxnAdminUser & {
    identityProviderName: string;
    organizationName: string;
    relyingParty: string;
    axnAdminFirstNameLastNameAndEmail: string;
};

export const OrganizationUsersTableContainer = (props: OrganizationUsersTableContainerProps) => {
    const {
        session,
        axnAdminUsers,
        axnRelyingParties,
        tableState,
        isPageHeight
    } = props;

    const dispatch = useDispatch();
    const getAxnAdminFirstNameLastNameAndEmail = (axnAdminUser: FormattedAxnAdminUserDetails) => {
        let axnAdminFirstNameLastNameAndEmail = "";

        if (axnAdminUser?.firstName) {
            axnAdminFirstNameLastNameAndEmail += axnAdminUser.firstName;
        }

        if (axnAdminUser?.lastName) {
            axnAdminFirstNameLastNameAndEmail += ` ${axnAdminUser.lastName}`;
        }

        if (axnAdminUser?.email) {
            axnAdminFirstNameLastNameAndEmail += ` ${axnAdminUser.email}`;
        }

        if (!axnAdminUser?.firstName && !axnAdminUser?.lastName && !axnAdminUser?.email) {
            axnAdminFirstNameLastNameAndEmail = resources.common.notFound;
        }

        return axnAdminFirstNameLastNameAndEmail;
    }

    const organizationUserTableData: Array<OrganizationUserTableDataRow> = useMemo(() => {
        const sessionUserAllowedRelyingParties = extractRelyingPartiesForPropertyName(
            UserPermissionTypes.LIST_USERS,
            session.user.axnAdminUserRPProperties,
            axnRelyingParties
        );
        const sessionUserRpIds = sessionUserAllowedRelyingParties.map(rp => rp.id);

        return axnAdminUsers.map(user => {

            let relyingPartyString = resources.common.notFound;

            if (user.axnAdminUserRPProperties.length > 0) {
                const userAllowedRelyingParties = extractRelyingPartiesForPropertyName(
                    UserPermissionTypes.VIEW_SERVICES,
                    user.axnAdminUserRPProperties,
                    axnRelyingParties
                );

                if (userAllowedRelyingParties.length > 0) {
                    const relyingPartiesForString: Array<string> = [];
                    userAllowedRelyingParties.forEach(rp => {
                        //if the user looking at the table is looking at their own account
                        //show all of the RPs they are a part of
                        //otherwise, filter by what the session user can see
                        if (user.id === session.user.id || sessionUserRpIds.includes(rp.id)) {
                            relyingPartiesForString.push(rp.name);
                        }
                    });

                    if (relyingPartiesForString.length > 0) {
                        relyingPartyString = relyingPartiesForString.join(", ");
                    }
                }
            }

            return {
                ...user,
                identityProviderName: user.axnAdminUserDetails?.identityProvider?.name || resources.common.notFound,
                organizationName: user.organization?.name || resources.common.notFound,
                relyingParty: relyingPartyString,
                roles: user.roles,
                axnAdminFirstNameLastNameAndEmail: getAxnAdminFirstNameLastNameAndEmail(user.axnAdminUserDetails)
            }
        });
    }, [axnAdminUsers]);

    const isAdminUser = useMemo(() => isAxnAdminUser(session.user), [session.user]);

    const columnHelper = createColumnHelper<OrganizationUserTableDataRow>();
    const columns = useMemo(() => {
        const finalColumns: AccessorKeyColumnDef<OrganizationUserTableDataRow, string | Date>[] = [
            columnHelper.accessor("axnAdminFirstNameLastNameAndEmail", {
                header: resources.tables.organizationUsers.columns.name.header,
                filterFn: "fuzzyText",
                sortingFn: (a, b) => sortStrings(a.original.axnAdminFirstNameLastNameAndEmail, b.original.axnAdminFirstNameLastNameAndEmail),
                cell: (props) => (
                    <OrganizationUsersTableCell
                        value={<OrganizationUsersNameEmailLabel
                            value={props.getValue()}
                            axnAdminUser={props.row.original.axnAdminUserDetails} />}
                        organizationUser={props.row.original}
                    />
                )
            }),
            columnHelper.accessor("roles", {
                header: resources.tables.organizationUsers.columns.roles.header,
                filterFn: "fuzzyText"
            })
        ];

        if (isAdminUser) {
            finalColumns.push(
                columnHelper.accessor("organizationName", {
                    header: resources.tables.organizationUsers.columns.organization.header,
                    filterFn: "fuzzyText",
                    sortingFn: (a, b) => sortStrings(a.original.organizationName, b.original.organizationName)
                })
            );
        }

        finalColumns.push(
            columnHelper.accessor("identityProviderName", {
                header: resources.tables.organizationUsers.columns.provider.header,
                filterFn: "fuzzyText",
                sortingFn: (a, b) => sortStrings(a.original.identityProviderName, b.original.identityProviderName)
            }),
            columnHelper.accessor("status", {
                header: resources.tables.organizationUsers.columns.status.header,
                filterFn: "singleSelectFilter",
                cell: (props) => {
                    switch (props.getValue()) {
                        case EntityStatusTypes.ACTIVE:
                            return <Badge bg={"success"}>{resources.common.active}</Badge>;
                        case EntityStatusTypes.INACTIVE:
                            return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                        default:
                            return <span>{resources.common.notFound}</span>;
                    }
                },
                meta: {
                    filterOptions: ENTITY_STATUS_OPTIONS
                },
            }),
            columnHelper.accessor("createdDate", {
                header: resources.tables.organizationUsers.columns.created.header,
                cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE)}</span> : null,
                filterFn: "dateTimeFilter",
                sortingFn: (a, b) => sortDates(a.original.createdDate, b.original.createdDate)
            })
        );

        return finalColumns;
    }, [organizationUserTableData, isAdminUser]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: organizationUserTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.ORGANIZATION_USERS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.ORGANIZATION_USERS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.ORGANIZATION_USERS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="organization-users"
                useTableResult={useTableResult}
                className={styles.organizationUsersTable}
                headerColumnKeyPrefix={"organization-users"}
            />
            <PaginationContainer
                idPrefix="organization-users"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};